import { useAtomValue } from 'jotai/react'
import type { ToasterProps } from 'sonner'
import { Toaster as Sonner } from 'sonner'
import { themeAtom } from '../../atoms/theme'
import { cn } from '../../lib/utils'

// The palette every toast renders in, regardless of the host app's `<body>`
// palette class. One edit here flips every app's toasts to another palette.
// Light/dark still follows the app: `.theme-ds` and `.dark .theme-ds` both
// define the surface tokens the toast reads.
const TOAST_PALETTE_CLASS = 'theme-ds'

/** Measured against a loading toast: the text's ink centre sat 2px above the
 *  spinner's. See the `classNames.content` note below for why. */
const CONTENT_OPTICAL_NUDGE = 'translate-y-[0.0625rem]'

const Toaster = ({ className, toastOptions, ...props }: ToasterProps) => {
  const theme = useAtomValue(themeAtom)

  return (
    <Sonner
      theme={theme as ToasterProps['theme']}
      position='bottom-right'
      className={cn('toaster group', TOAST_PALETTE_CLASS, className)}
      style={
        {
          // Surface tokens fork light/dark inside the palette (ui.css) so the
          // toast keeps reading as lifted above whichever app renders it.
          '--normal-bg': 'var(--toast-surface)',
          '--normal-text': 'var(--popover-foreground)',
          '--normal-border': 'var(--toast-surface-border)',
          // Move the close button to the top-right (sonner defaults to
          // top-left). These vars flip sonner's LTR default to its RTL layout.
          '--toast-close-button-start': 'auto',
          '--toast-close-button-end': '0',
          '--toast-close-button-transform': 'translate(35%, -35%)'
        } as React.CSSProperties
      }
      toastOptions={{
        ...toastOptions,
        classNames: {
          ...toastOptions?.classNames,
          // SportableBig leaves far more room below its baseline than above,
          // so its glyphs sit high in their own line box. Sonner centres the
          // boxes — icon and text agree to the pixel — but the text still
          // reads about 2px above a loading spinner, which is a circle
          // honestly centred in its own. Nudge the text onto the icon's
          // optical centre; `content` rather than `title` so a description
          // travels with it. Set after the spread, so a caller's own
          // `content` classes merge with the nudge instead of dropping it.
          content: cn(CONTENT_OPTICAL_NUDGE, toastOptions?.classNames?.content)
        },
        style: { fontFamily: 'SportableBig', ...toastOptions?.style }
      }}
      {...props}
    />
  )
}

export { Toaster }
